import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { sanitizeHtml } from '../utils/sanitize';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Scale, 
  BookOpen, 
  Lightbulb,
  Plus,
  Edit,
  Save,
  X,
  Upload,
  Trash2,
  FileUp,
  Loader2,
  FolderPlus,
  Settings,
  ChevronRight,
  Download,
  FileEdit,
  Send as SendIcon,
  Link,
  ExternalLink,
  Camera,
  Image as ImageIcon,
  Maximize2,
  Minimize2,
  Eye,
  Crop,
  Wand2,
  RotateCw,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const ICON_MAP = {
  Scale: Scale,
  BookOpen: BookOpen,
  Lightbulb: Lightbulb,
  FileText: FileText,
};

const DEFAULT_CATEGORIES = [
  { id: 'quick-guide', name: 'Quick Guide', slug: 'quick-guide', description: 'Quick reference guides', icon: 'Lightbulb', order: 0 },
  { id: 'security-plan', name: 'TC Security Plan', slug: 'security-plan', description: 'Truth Church Security Plan', icon: 'BookOpen', order: 1 },
  { id: 'crisis-management', name: 'TC Crisis Management', slug: 'crisis-management', description: 'Crisis management procedures', icon: 'Scale', order: 2 },
  { id: 'incident-report', name: 'Incident Report', slug: 'incident-report', description: 'Incident reporting forms', icon: 'FileText', order: 3 },
];

const DocumentsPage = () => {
  const { isSecurityHead, hasPermission } = useAuth();
  
  // Permission checks
  const canUpload = hasPermission('can_upload_documents');
  const canDelete = hasPermission('can_delete_documents');
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('quick-guide');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [fillableFormOpen, setFillableFormOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: '', content: '', category: '' });
  const [uploadData, setUploadData] = useState({ file: null, title: '', category: 'quick-guide' });
  const [linkData, setLinkData] = useState({ title: '', url: '', description: '', category: 'quick-guide' });
  const [newCategory, setNewCategory] = useState({ name: '', slug: '', description: '', icon: 'FileText' });
  const [formFields, setFormFields] = useState({});
  const [scanData, setScanData] = useState({ 
    images: [], 
    title: '', 
    notes: '', 
    email: '',
    category: 'quick-guide'
  });
  const [isCapturing, setIsCapturing] = useState(false);
  const [autoCapture, setAutoCapture] = useState(false);
  const [countdown, setCountdown] = useState(0);
  // Document Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFullscreen, setViewerFullscreen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  // Crop/Edit state for scanned images
  const [cropMode, setCropMode] = useState(false);
  const [cropImageIndex, setCropImageIndex] = useState(null);
  const [cropPoints, setCropPoints] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  // Corner detection state
  const [detectedCorners, setDetectedCorners] = useState(null);
  const [cornerDetectionEnabled, setCornerDetectionEnabled] = useState(true);
  const fileInputRef = useRef(null);
  const autoCapTimerRef = useRef(null);
  const scanInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const cornerDetectionRef = useRef(null);

  useEffect(() => {
    fetchCategories();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/document-categories`);
      if (response.data && response.data.length > 0) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/documents`);
      setDocuments(response.data);
      const categoryDocs = response.data.filter(d => d.category === selectedCategory);
      if (categoryDocs.length > 0 && !selectedDoc) {
        setSelectedDoc(categoryDocs[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async () => {
    if (!newCategory.name || !newCategory.slug) {
      toast.error('Please fill name and slug');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/document-categories`, newCategory);
      setCategories([...categories, response.data]);
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', slug: '', description: '', icon: 'FileText' });
      toast.success('Category created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Are you sure? This will NOT delete documents in this category.')) return;
    
    try {
      await axios.delete(`${API_URL}/document-categories/${catId}`);
      setCategories(categories.filter(c => c.id !== catId));
      if (selectedCategory === catId) {
        setSelectedCategory('quick-guide');
      }
      toast.success('Category deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const createDocument = async () => {
    if (!newDoc.title || !newDoc.content || !newDoc.category) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/documents`, newDoc);
      toast.success('Document created');
      setDialogOpen(false);
      setNewDoc({ title: '', content: '', category: '' });
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to create document');
    }
  };

  const createLink = async () => {
    if (!linkData.title || !linkData.url || !linkData.category) {
      toast.error('Please fill title and URL');
      return;
    }

    // Validate URL format
    try {
      new URL(linkData.url);
    } catch {
      toast.error('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    try {
      // Format content with link marker and optional description
      const content = `[LINK]${linkData.url}[/LINK]${linkData.description ? `\n\n${linkData.description}` : ''}`;
      
      await axios.post(`${API_URL}/documents`, {
        title: linkData.title,
        content: content,
        category: linkData.category
      });
      toast.success('Link added!');
      setLinkDialogOpen(false);
      setLinkData({ title: '', url: '', description: '', category: 'quick-guide' });
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to add link');
    }
  };

  const isLinkDocument = (doc) => {
    return doc?.content?.startsWith('[LINK]');
  };

  const extractLinkUrl = (content) => {
    const match = content?.match(/\[LINK\](.*?)\[\/LINK\]/);
    return match ? match[1] : null;
  };

  const extractLinkDescription = (content) => {
    const parts = content?.split('[/LINK]');
    return parts && parts[1] ? parts[1].trim() : '';
  };

  // Download document as text/markdown file
  const downloadDocument = (doc) => {
    if (!doc) return;
    
    let content = doc.content;
    let filename = `${doc.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    let mimeType = 'text/plain';
    
    // For HTML content, download as HTML
    if (doc.content_type === 'html') {
      filename = `${doc.title.replace(/[^a-z0-9]/gi, '_')}.html`;
      mimeType = 'text/html';
    }
    
    // For link documents, create a simple text file with the link
    if (isLinkDocument(doc)) {
      const url = extractLinkUrl(doc.content);
      const desc = extractLinkDescription(doc.content);
      content = `${doc.title}\n\nLink: ${url}${desc ? `\n\nDescription: ${desc}` : ''}`;
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Document downloaded!');
  };

  // Download as PDF (using print to PDF)
  const downloadAsPdf = (doc) => {
    if (!doc) return;
    
    // Create a new window with the content for printing
    const printWindow = window.open('', '_blank');
    
    let content = doc.content;
    if (doc.content_type === 'html') {
      // Already HTML, use as is
    } else if (isLinkDocument(doc)) {
      const url = extractLinkUrl(doc.content);
      const desc = extractLinkDescription(doc.content);
      content = `<h1>${doc.title}</h1><p><strong>Link:</strong> <a href="${url}">${url}</a></p>${desc ? `<p>${desc}</p>` : ''}`;
    } else {
      // Convert markdown-style content to basic HTML
      content = `<pre style="white-space: pre-wrap; font-family: Arial, sans-serif;">${doc.content}</pre>`;
    }

    // Sanitize before writing into the print window — defense in depth even
    // though the source is admin-authored.
    const safeContent = sanitizeHtml(content);
    const safeTitle = sanitizeHtml(doc.title);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
            h2 { color: #555; margin-top: 20px; }
            p { line-height: 1.6; }
            pre { background: #f5f5f5; padding: 15px; border-radius: 5px; overflow-x: auto; }
          </style>
        </head>
        <body>
          <h1>${safeTitle}</h1>
          ${safeContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
    };
    
    toast.info('Use "Save as PDF" in the print dialog');
  };

  // Email/submit document
  const submitDocument = (doc) => {
    if (!doc) return;
    
    const subject = encodeURIComponent(`Document: ${doc.title}`);
    const body = encodeURIComponent(`Please find the attached document:\n\n${doc.title}\n\n---\n\n${doc.content.substring(0, 500)}...`);
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.info('Email client opened');
  };

  // Open document in viewer
  const openInViewer = (doc) => {
    setViewerDoc(doc);
    setViewerOpen(true);
  };

  // ============ SCAN FEATURE ============
  
  // Simple edge/corner detection using contrast analysis
  const detectDocumentCorners = (video, canvas) => {
    if (!video || !canvas) return null;
    
    const ctx = canvas.getContext('2d');
    const width = video.videoWidth;
    const height = video.videoHeight;
    
    if (width === 0 || height === 0) return null;
    
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(video, 0, 0);
    
    // Get image data for edge detection
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    // Convert to grayscale and detect edges using simple Sobel-like operator
    const gray = new Uint8Array(width * height);
    for (let i = 0; i < data.length; i += 4) {
      gray[i / 4] = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
    }
    
    // Find strong horizontal and vertical edges
    const edges = new Uint8Array(width * height);
    const threshold = 30;
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = y * width + x;
        const gx = Math.abs(gray[idx + 1] - gray[idx - 1]);
        const gy = Math.abs(gray[idx + width] - gray[idx - width]);
        edges[idx] = (gx + gy > threshold) ? 255 : 0;
      }
    }
    
    // Find corners by scanning from edges
    const margin = Math.min(width, height) * 0.1;
    const scanSize = Math.min(width, height) * 0.3;
    
    // Helper to find strongest edge point in a region
    const findCorner = (startX, startY, dirX, dirY) => {
      let bestX = startX, bestY = startY, bestScore = 0;
      
      for (let dy = 0; dy < scanSize; dy++) {
        for (let dx = 0; dx < scanSize; dx++) {
          const x = Math.floor(startX + dx * dirX);
          const y = Math.floor(startY + dy * dirY);
          
          if (x < 0 || x >= width || y < 0 || y >= height) continue;
          
          const idx = y * width + x;
          if (edges[idx] > 0) {
            // Score based on position (prefer corners closer to expected position)
            const score = edges[idx] + (dx + dy) * 0.5;
            if (score > bestScore) {
              bestScore = score;
              bestX = x;
              bestY = y;
            }
          }
        }
      }
      
      return bestScore > 0 ? { x: bestX, y: bestY } : null;
    };
    
    // Detect four corners
    const topLeft = findCorner(margin, margin, 1, 1);
    const topRight = findCorner(width - margin, margin, -1, 1);
    const bottomRight = findCorner(width - margin, height - margin, -1, -1);
    const bottomLeft = findCorner(margin, height - margin, 1, -1);
    
    if (topLeft && topRight && bottomRight && bottomLeft) {
      return [
        { x: (topLeft.x / width) * 100, y: (topLeft.y / height) * 100 },
        { x: (topRight.x / width) * 100, y: (topRight.y / height) * 100 },
        { x: (bottomRight.x / width) * 100, y: (bottomRight.y / height) * 100 },
        { x: (bottomLeft.x / width) * 100, y: (bottomLeft.y / height) * 100 }
      ];
    }
    
    return null;
  };

  // Start corner detection loop
  const startCornerDetection = () => {
    if (cornerDetectionRef.current) return;
    
    const detectLoop = () => {
      if (videoRef.current && canvasRef.current && isCapturing && cornerDetectionEnabled) {
        const corners = detectDocumentCorners(videoRef.current, canvasRef.current);
        setDetectedCorners(corners);
      }
      cornerDetectionRef.current = requestAnimationFrame(detectLoop);
    };
    
    cornerDetectionRef.current = requestAnimationFrame(detectLoop);
  };

  // Stop corner detection
  const stopCornerDetection = () => {
    if (cornerDetectionRef.current) {
      cancelAnimationFrame(cornerDetectionRef.current);
      cornerDetectionRef.current = null;
    }
    setDetectedCorners(null);
  };
  
  // Start camera for scanning
  const startCamera = async () => {
    try {
      setIsCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera for document scanning
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Start corner detection after video loads
        videoRef.current.onloadedmetadata = () => {
          startCornerDetection();
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Could not access camera. Please use file upload instead.');
      setIsCapturing(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    stopCornerDetection();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    stopAutoCapture();
    setIsCapturing(false);
    setDetectedCorners(null);
  };

  // Capture image from camera with optional auto-crop
  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setScanData(prev => ({
      ...prev,
      images: [...prev.images, imageData]
    }));
    
    toast.success(`Page ${scanData.images.length + 1} captured!`);
  };

  // Start auto-capture with countdown
  const startAutoCapture = () => {
    // Clear any existing timer first
    if (autoCapTimerRef.current) {
      clearInterval(autoCapTimerRef.current);
    }
    
    setAutoCapture(true);
    setCountdown(3);
    
    // Countdown timer
    let count = 3;
    autoCapTimerRef.current = setInterval(() => {
      count--;
      setCountdown(count);
      
      if (count <= 0) {
        // Capture the image
        captureImage();
        // Reset for next capture
        count = 3;
        setCountdown(3);
      }
    }, 1000);
  };

  // Stop auto-capture - FIXED
  const stopAutoCapture = () => {
    console.log('Stopping auto capture, timer ref:', autoCapTimerRef.current);
    if (autoCapTimerRef.current) {
      clearInterval(autoCapTimerRef.current);
      autoCapTimerRef.current = null;
    }
    setAutoCapture(false);
    setCountdown(0);
  };

  // Handle file selection for scan
  const handleScanFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setScanData(prev => ({
            ...prev,
            images: [...prev.images, event.target.result]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
    toast.success(`${files.length} image(s) added`);
  };

  // Remove scanned image
  const removeScannedImage = (index) => {
    setScanData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Open crop mode for an image
  const openCropMode = (index) => {
    setCropImageIndex(index);
    setCropPoints([]);
    setCropMode(true);
  };

  // Handle crop canvas click to add corner points
  const handleCropClick = (e) => {
    if (!cropCanvasRef.current) return;
    
    const rect = cropCanvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    if (cropPoints.length < 4) {
      setCropPoints([...cropPoints, { x, y }]);
    }
  };

  // Apply perspective crop to image
  const applyCrop = () => {
    if (cropPoints.length !== 4 || cropImageIndex === null) {
      toast.error('Please select 4 corner points');
      return;
    }

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Sort points to get proper corners (top-left, top-right, bottom-right, bottom-left)
      const sortedPoints = [...cropPoints].sort((a, b) => a.y - b.y);
      const topPoints = sortedPoints.slice(0, 2).sort((a, b) => a.x - b.x);
      const bottomPoints = sortedPoints.slice(2, 4).sort((a, b) => a.x - b.x);
      const corners = [topPoints[0], topPoints[1], bottomPoints[1], bottomPoints[0]];
      
      // Convert percentage to actual coordinates
      const pts = corners.map(p => ({
        x: (p.x / 100) * img.width,
        y: (p.y / 100) * img.height
      }));
      
      // Calculate output dimensions
      const width = Math.max(
        Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y),
        Math.hypot(pts[2].x - pts[3].x, pts[2].y - pts[3].y)
      );
      const height = Math.max(
        Math.hypot(pts[3].x - pts[0].x, pts[3].y - pts[0].y),
        Math.hypot(pts[2].x - pts[1].x, pts[2].y - pts[1].y)
      );
      
      canvas.width = width;
      canvas.height = height;
      
      // Simple crop (for basic perspective, we'll use clip path)
      // For more advanced perspective transform, would need a library
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(width, 0);
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      ctx.clip();
      
      // Draw the cropped region
      const minX = Math.min(...pts.map(p => p.x));
      const minY = Math.min(...pts.map(p => p.y));
      const maxX = Math.max(...pts.map(p => p.x));
      const maxY = Math.max(...pts.map(p => p.y));
      
      ctx.drawImage(
        img,
        minX, minY, maxX - minX, maxY - minY,
        0, 0, width, height
      );
      
      // Apply contrast enhancement for document scan
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const contrast = 1.2; // Increase contrast
      const brightness = 10; // Slight brightness boost
      
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));
        data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness));
        data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness));
      }
      ctx.putImageData(imageData, 0, 0);
      
      const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
      
      // Update the image
      setScanData(prev => ({
        ...prev,
        images: prev.images.map((img, i) => i === cropImageIndex ? croppedImage : img)
      }));
      
      setCropMode(false);
      setCropImageIndex(null);
      setCropPoints([]);
      toast.success('Image cropped and enhanced!');
    };
    img.src = scanData.images[cropImageIndex];
  };

  // Auto-enhance image (contrast, brightness, sharpen)
  const autoEnhanceImage = (index) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Auto-levels: find min/max and stretch
      let minR = 255, maxR = 0, minG = 255, maxG = 0, minB = 255, maxB = 0;
      for (let i = 0; i < data.length; i += 4) {
        minR = Math.min(minR, data[i]);
        maxR = Math.max(maxR, data[i]);
        minG = Math.min(minG, data[i + 1]);
        maxG = Math.max(maxG, data[i + 1]);
        minB = Math.min(minB, data[i + 2]);
        maxB = Math.max(maxB, data[i + 2]);
      }
      
      // Apply auto-levels with slight contrast boost
      const contrast = 1.15;
      for (let i = 0; i < data.length; i += 4) {
        data[i] = Math.min(255, Math.max(0, ((data[i] - minR) / (maxR - minR || 1) * 255 - 128) * contrast + 128));
        data[i + 1] = Math.min(255, Math.max(0, ((data[i + 1] - minG) / (maxG - minG || 1) * 255 - 128) * contrast + 128));
        data[i + 2] = Math.min(255, Math.max(0, ((data[i + 2] - minB) / (maxB - minB || 1) * 255 - 128) * contrast + 128));
      }
      
      ctx.putImageData(imageData, 0, 0);
      const enhancedImage = canvas.toDataURL('image/jpeg', 0.9);
      
      setScanData(prev => ({
        ...prev,
        images: prev.images.map((img, i) => i === index ? enhancedImage : img)
      }));
      
      toast.success('Image enhanced!');
    };
    img.src = scanData.images[index];
  };

  // Submit scanned document via email with actual image attachments
  const submitScannedDocument = async () => {
    if (scanData.images.length === 0) {
      toast.error('Please scan or upload at least one image');
      return;
    }
    if (!scanData.title) {
      toast.error('Please enter a document title');
      return;
    }
    if (!scanData.email) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingEmail(true);
    
    try {
      // Upload images to backend and get URLs
      const uploadedUrls = [];
      for (let i = 0; i < scanData.images.length; i++) {
        // Convert base64 to blob
        const base64Data = scanData.images[i].split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let j = 0; j < byteCharacters.length; j++) {
          byteNumbers[j] = byteCharacters.charCodeAt(j);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        
        const formData = new FormData();
        formData.append('file', blob, `scan_page_${i + 1}.jpg`);
        formData.append('title', `${scanData.title} - Page ${i + 1}`);
        formData.append('category', scanData.category);
        
        const response = await axios.post(`${API_URL}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.file_url) {
          uploadedUrls.push(response.data.file_url);
        }
      }
      
      // Now send email with attachment links
      const subject = encodeURIComponent(`Scanned Document: ${scanData.title}`);
      const imageLinks = uploadedUrls.map((url, i) => `Page ${i + 1}: ${url}`).join('\n');
      const body = encodeURIComponent(
        `Scanned Document Submission\n\n` +
        `Title: ${scanData.title}\n` +
        `Number of Pages: ${scanData.images.length}\n` +
        `Category: ${scanData.category}\n` +
        `${scanData.notes ? `Notes: ${scanData.notes}\n` : ''}` +
        `\n---\nScanned Images:\n${imageLinks}\n\n` +
        `Submitted via Truth Church Security System`
      );
      
      window.location.href = `mailto:${scanData.email}?subject=${subject}&body=${body}`;
      toast.success('Email prepared with image links!');
      
      // Reset and close
      setScanDialogOpen(false);
      setScanData({ images: [], title: '', notes: '', email: '', category: 'quick-guide' });
      stopCamera();
      fetchDocuments();
    } catch (error) {
      console.error('Email submission error:', error);
      toast.error('Failed to process images. Try downloading as PDF instead.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Save scanned images as document
  const saveScannedDocument = async () => {
    if (scanData.images.length === 0) {
      toast.error('Please scan or upload at least one image');
      return;
    }
    if (!scanData.title) {
      toast.error('Please enter a document title');
      return;
    }

    try {
      // Create document with image references
      const content = `[SCANNED DOCUMENT]\n\nTitle: ${scanData.title}\nPages: ${scanData.images.length}\n${scanData.notes ? `Notes: ${scanData.notes}` : ''}\n\n[Images stored locally - ${scanData.images.length} page(s)]`;
      
      await axios.post(`${API_URL}/documents`, {
        title: scanData.title,
        content: content,
        category: scanData.category
      });
      
      toast.success('Scanned document saved!');
      setScanDialogOpen(false);
      setScanData({ images: [], title: '', notes: '', email: '', category: 'quick-guide' });
      stopCamera();
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to save document');
    }
  };

  // Download scanned images as PDF
  const downloadScannedAsPdf = () => {
    if (scanData.images.length === 0) {
      toast.error('No images to download');
      return;
    }

    const printWindow = window.open('', '_blank');
    const imagesHtml = scanData.images.map((img, i) => 
      `<div style="page-break-after: ${i < scanData.images.length - 1 ? 'always' : 'auto'}; text-align: center;">
        <img src="${img}" style="max-width: 100%; max-height: 90vh; object-fit: contain;" />
        <p style="font-size: 12px; color: #666;">Page ${i + 1} of ${scanData.images.length}</p>
      </div>`
    ).join('');

    const safeTitle = sanitizeHtml(scanData.title || 'Scanned Document');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeTitle}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1 style="text-align: center; margin-bottom: 20px;">${safeTitle}</h1>
          ${imagesHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
    toast.info('Use "Save as PDF" in print dialog');
  };

  // Clean up camera on dialog close
  const handleScanDialogClose = (open) => {
    if (!open) {
      stopCamera();
    }
    setScanDialogOpen(open);
  };

  const uploadDocument = async () => {
    if (!uploadData.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('category', uploadData.category);
      if (uploadData.title) {
        formData.append('title', uploadData.title);
      }

      const response = await axios.post(`${API_URL}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded!');
      setUploadDialogOpen(false);
      setUploadData({ file: null, title: '', category: 'quick-guide' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
      
      setSelectedCategory(response.data.category);
      setSelectedDoc(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const updateDocument = async () => {
    if (!selectedDoc) return;

    try {
      await axios.put(`${API_URL}/documents/${selectedDoc.id}`, {
        title: selectedDoc.title,
        content: selectedDoc.content,
        category: selectedDoc.category
      });
      toast.success('Document updated');
      setEditMode(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API_URL}/documents/${docId}`);
      toast.success('Document deleted');
      setSelectedDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const categoryDocs = documents.filter(d => d.category === selectedCategory);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file, title: file.name.split('.')[0].replace(/[-_]/g, ' ') });
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || FileText;
    return IconComponent;
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="documents-page">
      {/* Header with Logo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <PageHeader 
          title="Documentation" 
          subtitle="Truth Church: Policies, Rules, Reference Guides and Incident Reports"
        />
        
        {canUpload && (
          <div className="flex gap-2 flex-wrap">
            {/* Add Category Dialog */}
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-white/20 hover:bg-white/5 font-bold uppercase tracking-wide rounded-sm"
                  data-testid="add-category-btn"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Add Tab
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">
                    Add Document Category
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Category Name *</Label>
                    <Input
                      placeholder="e.g., Procedures"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Slug (URL-friendly) *</Label>
                    <Input
                      placeholder="e.g., procedures"
                      value={newCategory.slug}
                      onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      className="bg-black border-white/20 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Description</Label>
                    <Input
                      placeholder="Brief description"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Icon</Label>
                    <Select value={newCategory.icon} onValueChange={(v) => setNewCategory({ ...newCategory, icon: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        <SelectItem value="FileText">Document</SelectItem>
                        <SelectItem value="Scale">Scale (Law)</SelectItem>
                        <SelectItem value="BookOpen">Book</SelectItem>
                        <SelectItem value="Lightbulb">Lightbulb</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={createCategory}
                    className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                  >
                    Create Category
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Upload Document Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-white/20 hover:bg-white/5 font-bold uppercase tracking-wide rounded-sm"
                  data-testid="upload-doc-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">
                    Upload Document
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div 
                    className="border-2 border-dashed border-white/20 rounded-sm p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.doc,.rtf,.txt,.md,.html,.htm,.png,.jpg,.jpeg"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <FileUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    {uploadData.file ? (
                      <div>
                        <p className="font-inter text-sm text-white">{uploadData.file.name}</p>
                        <p className="font-mono text-xs text-muted-foreground mt-1">
                          {(uploadData.file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-inter text-sm text-muted-foreground">
                          Click to select or drag & drop
                        </p>
                        <p className="font-mono text-xs text-muted-foreground mt-1">
                          Word (.docx, .doc), PDF, RTF, TXT, HTML, Images
                        </p>
                        <p className="font-mono text-[10px] text-primary/70 mt-2">
                          Word documents preserve formatting automatically
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Title (Optional)</Label>
                    <Input
                      placeholder="Auto-generated from filename"
                      value={uploadData.title}
                      onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Category</Label>
                    <Select value={uploadData.category} onValueChange={(v) => setUploadData({ ...uploadData, category: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="font-mono text-sm">
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={uploadDocument}
                    disabled={!uploadData.file || uploading}
                    className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                    data-testid="submit-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload & Extract
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Link Dialog */}
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-white/20 hover:bg-white/5 font-bold uppercase tracking-wide rounded-sm"
                  data-testid="add-link-btn"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Add Link
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">
                    Add External Link
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Title *</Label>
                    <Input
                      placeholder="e.g., State Fire Code Regulations"
                      value={linkData.title}
                      onChange={(e) => setLinkData({ ...linkData, title: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">URL *</Label>
                    <Input
                      placeholder="https://example.com/document"
                      value={linkData.url}
                      onChange={(e) => setLinkData({ ...linkData, url: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Description (Optional)</Label>
                    <Textarea
                      placeholder="Brief description of what this link contains..."
                      value={linkData.description}
                      onChange={(e) => setLinkData({ ...linkData, description: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-inter text-sm min-h-[80px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Category</Label>
                    <Select value={linkData.category} onValueChange={(v) => setLinkData({ ...linkData, category: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="font-mono text-sm">
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={createLink}
                    disabled={!linkData.title || !linkData.url}
                    className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                    data-testid="submit-link-btn"
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Add Link
                  </Button>
                </div>
              </DialogContent>
            </Dialog>


            {/* Create Document Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wide rounded-sm"
                  data-testid="add-doc-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-panel border-white/10 rounded-sm max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl text-white">
                    Create Document
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Category</Label>
                    <Select value={newDoc.category} onValueChange={(v) => setNewDoc({ ...newDoc, category: v })}>
                      <SelectTrigger className="bg-black border-white/20 rounded-sm">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="font-mono text-sm">
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Title</Label>
                    <Input
                      placeholder="Document title"
                      value={newDoc.title}
                      onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Content (Markdown)</Label>
                    <Textarea
                      placeholder="Document content..."
                      value={newDoc.content}
                      onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm min-h-[300px]"
                    />
                  </div>
                  <Button 
                    onClick={createDocument}
                    className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                  >
                    Create Document
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 bg-panel border border-white/10 rounded-sm p-2">
        {categories.map((cat) => {
          const IconComponent = getIcon(cat.icon);
          const isActive = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedCategory(cat.id);
                const docs = documents.filter(d => d.category === cat.id);
                if (docs.length > 0) setSelectedDoc(docs[0]);
                else setSelectedDoc(null);
                setEditMode(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm font-mono text-xs uppercase transition-colors ${
                isActive 
                  ? 'bg-white text-black' 
                  : 'text-muted-foreground hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${cat.id}`}
            >
              <IconComponent className="w-4 h-4" />
              <span className="hidden sm:inline">{cat.name}</span>
              {canDelete && !['compliance', 'rules', 'guides'].includes(cat.id) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCategory(cat.id);
                  }}
                  className="ml-1 p-0.5 hover:bg-black/20 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </button>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Document List */}
        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
              Documents ({categoryDocs.length})
            </CardTitle>
          </CardHeader>
          <ScrollArea className="h-[500px]">
            <CardContent className="p-2">
              {categoryDocs.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="font-mono text-sm text-muted-foreground">No documents</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {categoryDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-2 p-3 rounded-sm transition-colors ${
                        selectedDoc?.id === doc.id 
                          ? 'bg-primary/20 border border-primary/50' 
                          : 'hover:bg-white/5'
                      }`}
                      data-testid={`doc-${doc.id}`}
                    >
                      <button
                        onClick={() => {
                          setSelectedDoc(doc);
                          setEditMode(false);
                        }}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="flex items-center gap-2">
                          {isLinkDocument(doc) && (
                            <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                          )}
                          <p className="font-inter text-sm text-white truncate">{doc.title}</p>
                        </div>
                        <p className="font-mono text-[10px] text-muted-foreground mt-1">
                          {isLinkDocument(doc) ? 'External Link' : `Updated ${formatDate(doc.updated_at)}`}
                        </p>
                        {doc.source_file && (
                          <p className="font-mono text-[10px] text-primary/70 mt-0.5 truncate">
                            📎 {doc.source_file}
                          </p>
                        )}
                      </button>
                      {/* View & Download buttons */}
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            openInViewer(doc);
                          }}
                          className="h-8 w-8 text-primary hover:text-white hover:bg-primary/20"
                          title="Open in Viewer"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadDocument(doc);
                          }}
                          className="h-8 w-8 text-muted-foreground hover:text-white hover:bg-white/10"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </ScrollArea>
        </Card>

        {/* Document Content */}
        <Card className="bg-panel border-white/10 rounded-sm lg:col-span-3">
          {selectedDoc ? (
            <>
              <CardHeader className="border-b border-white/10 pb-4">
                <div className="flex items-center justify-between gap-4">
                  {editMode ? (
                    <Input
                      value={selectedDoc.title}
                      onChange={(e) => setSelectedDoc({ ...selectedDoc, title: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-chivo font-bold text-lg flex-1"
                    />
                  ) : (
                    <CardTitle className="font-chivo font-bold text-xl text-white truncate">
                      {selectedDoc.title}
                    </CardTitle>
                  )}
                  {canUpload && (
                    <div className="flex gap-2 shrink-0">
                      {editMode ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditMode(false)}
                            className="border-white/20 rounded-sm"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={updateDocument}
                            className="bg-green-600 hover:bg-green-700 rounded-sm"
                            data-testid="save-doc-btn"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Download/Submit buttons */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadDocument(selectedDoc)}
                            className="border-white/20 rounded-sm"
                            title="Download as text file"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadAsPdf(selectedDoc)}
                            className="border-white/20 rounded-sm"
                            title="Download as PDF"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFillableFormOpen(true)}
                            className="border-primary/50 text-primary hover:bg-primary/10 rounded-sm"
                            title="Fill & Submit Form"
                          >
                            <FileEdit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => submitDocument(selectedDoc)}
                            className="border-green-600/50 text-green-500 hover:bg-green-600/10 rounded-sm"
                            title="Email document"
                          >
                            <SendIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteDocument(selectedDoc.id)}
                            className="border-alert-red/50 text-alert-red hover:bg-alert-red/10 rounded-sm"
                            data-testid="delete-doc-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditMode(true)}
                            className="border-white/20 rounded-sm"
                            data-testid="edit-doc-btn"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <ScrollArea className="h-[500px]">
                <CardContent className="p-6">
                  {editMode ? (
                    <Textarea
                      value={selectedDoc.content}
                      onChange={(e) => setSelectedDoc({ ...selectedDoc, content: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm min-h-[400px]"
                    />
                  ) : isLinkDocument(selectedDoc) ? (
                    <div className="space-y-6">
                      <div className="bg-surface rounded-sm p-6 border border-white/10">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-sm bg-primary/20 flex items-center justify-center">
                            <ExternalLink className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-mono text-xs text-muted-foreground uppercase">External Link</p>
                            <p className="font-inter text-sm text-white truncate max-w-md">
                              {extractLinkUrl(selectedDoc.content)}
                            </p>
                          </div>
                        </div>
                        <a
                          href={extractLinkUrl(selectedDoc.content)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-sm font-bold uppercase text-sm transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open Link
                        </a>
                      </div>
                      {extractLinkDescription(selectedDoc.content) && (
                        <div className="prose prose-invert prose-sm max-w-none">
                          <h3 className="font-chivo font-bold text-lg text-white mb-2">Description</h3>
                          <p className="font-inter text-sm text-muted-foreground">
                            {extractLinkDescription(selectedDoc.content)}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="prose prose-invert prose-sm max-w-none">
                      {selectedDoc.content_type === 'html' ? (
                        <HtmlRenderer content={selectedDoc.content} />
                      ) : (
                        <MarkdownRenderer content={selectedDoc.content} />
                      )}
                    </div>
                  )}
                </CardContent>
              </ScrollArea>
            </>
          ) : (
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="font-chivo font-bold text-xl text-white">Select a Document</p>
              <p className="font-inter text-sm text-muted-foreground mt-1">
                Choose a document from the list to view
              </p>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Fillable Form Dialog */}
      <Dialog open={fillableFormOpen} onOpenChange={setFillableFormOpen}>
        <DialogContent className="bg-panel border-white/10 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold text-xl text-white flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-primary" />
              Fill & Submit: {selectedDoc?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Dynamic form fields based on document content */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Your Name *</Label>
                <Input
                  value={formFields.name || ''}
                  onChange={(e) => setFormFields({ ...formFields, name: e.target.value })}
                  placeholder="Enter your full name"
                  className="bg-black border-white/20 rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Date *</Label>
                <Input
                  type="date"
                  value={formFields.date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormFields({ ...formFields, date: e.target.value })}
                  className="bg-black border-white/20 rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Role/Position</Label>
                <Input
                  value={formFields.role || ''}
                  onChange={(e) => setFormFields({ ...formFields, role: e.target.value })}
                  placeholder="e.g., Security Team Member"
                  className="bg-black border-white/20 rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Notes / Acknowledgement</Label>
                <Textarea
                  value={formFields.notes || ''}
                  onChange={(e) => setFormFields({ ...formFields, notes: e.target.value })}
                  placeholder="Add any notes or confirm you have read and understood this document..."
                  className="bg-black border-white/20 rounded-sm font-inter text-sm min-h-[100px]"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase text-muted-foreground">Digital Signature</Label>
                <Input
                  value={formFields.signature || ''}
                  onChange={(e) => setFormFields({ ...formFields, signature: e.target.value })}
                  placeholder="Type your full name as signature"
                  className="bg-black border-white/20 rounded-sm font-cursive italic"
                />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => {
                  // Generate filled PDF
                  const printWindow = window.open('', '_blank');
                  // Sanitize every interpolation — formFields.* are user input
                  // and selectedDoc is admin-authored but stored externally.
                  const s = (v) => sanitizeHtml(v ?? '');
                  printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <title>${s(selectedDoc?.title)} - Filled Form</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                          h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
                          .field { margin: 20px 0; padding: 10px; background: #f5f5f5; border-radius: 5px; }
                          .label { font-weight: bold; color: #555; font-size: 12px; text-transform: uppercase; }
                          .value { margin-top: 5px; font-size: 16px; }
                          .signature { font-style: italic; font-family: cursive; font-size: 24px; border-bottom: 1px solid #333; padding: 10px 0; margin-top: 30px; }
                          .document-content { border: 1px solid #ddd; padding: 20px; margin: 20px 0; background: #fafafa; }
                        </style>
                      </head>
                      <body>
                        <h1>${s(selectedDoc?.title)}</h1>
                        <div class="document-content">
                          <pre style="white-space: pre-wrap;">${s(selectedDoc?.content?.substring(0, 2000))}...</pre>
                        </div>
                        <h2>Form Submission</h2>
                        <div class="field"><div class="label">Name</div><div class="value">${s(formFields.name) || 'N/A'}</div></div>
                        <div class="field"><div class="label">Date</div><div class="value">${s(formFields.date) || 'N/A'}</div></div>
                        <div class="field"><div class="label">Role/Position</div><div class="value">${s(formFields.role) || 'N/A'}</div></div>
                        <div class="field"><div class="label">Notes</div><div class="value">${s(formFields.notes) || 'N/A'}</div></div>
                        <div class="signature">${s(formFields.signature)}</div>
                        <p style="font-size: 12px; color: #666; margin-top: 10px;">Digital Signature</p>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.onload = () => printWindow.print();
                  toast.info('Use "Save as PDF" in print dialog');
                }}
                className="flex-1 bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                disabled={!formFields.name || !formFields.signature}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Filled PDF
              </Button>
              <Button
                onClick={() => {
                  const subject = encodeURIComponent(`Form Submission: ${selectedDoc?.title}`);
                  const body = encodeURIComponent(
                    `Form Submission for: ${selectedDoc?.title}\n\n` +
                    `Name: ${formFields.name || 'N/A'}\n` +
                    `Date: ${formFields.date || 'N/A'}\n` +
                    `Role: ${formFields.role || 'N/A'}\n` +
                    `Notes: ${formFields.notes || 'N/A'}\n\n` +
                    `Digital Signature: ${formFields.signature || 'N/A'}\n\n` +
                    `---\nSubmitted electronically via Truth Church Security System`
                  );
                  window.location.href = `mailto:?subject=${subject}&body=${body}`;
                  toast.success('Email client opened for submission');
                  setFillableFormOpen(false);
                }}
                variant="outline"
                className="flex-1 border-green-600/50 text-green-500 hover:bg-green-600/10 font-bold uppercase rounded-sm"
                disabled={!formFields.name || !formFields.signature}
              >
                <SendIcon className="w-4 h-4 mr-2" />
                Submit via Email
              </Button>
            </div>
            <p className="font-mono text-xs text-muted-foreground text-center">
              * Name and signature required to submit
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent 
          className={`bg-panel border-white/10 rounded-sm ${
            viewerFullscreen 
              ? 'fixed inset-4 max-w-none max-h-none w-auto h-auto' 
              : 'max-w-4xl max-h-[90vh]'
          } overflow-hidden flex flex-col`}
        >
          <DialogHeader className="flex-shrink-0 border-b border-white/10 pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="font-chivo font-bold text-xl text-white truncate pr-4">
                {viewerDoc?.title || 'Document Viewer'}
              </DialogTitle>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => downloadDocument(viewerDoc)}
                  className="border-white/20 rounded-sm"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setViewerFullscreen(!viewerFullscreen)}
                  className="border-white/20 rounded-sm"
                  title={viewerFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {viewerFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <ScrollArea className="flex-1 p-6">
            {viewerDoc && (
              <div className="prose prose-invert prose-sm max-w-none">
                {isLinkDocument(viewerDoc) ? (
                  <div className="space-y-6">
                    <div className="bg-surface rounded-sm p-6 border border-white/10">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-sm bg-primary/20 flex items-center justify-center">
                          <ExternalLink className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-mono text-xs text-muted-foreground uppercase">External Link</p>
                          <p className="font-inter text-sm text-white truncate max-w-md">
                            {extractLinkUrl(viewerDoc.content)}
                          </p>
                        </div>
                      </div>
                      <a
                        href={extractLinkUrl(viewerDoc.content)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-sm font-bold uppercase text-sm transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open Link
                      </a>
                    </div>
                    {extractLinkDescription(viewerDoc.content) && (
                      <div>
                        <h3 className="font-chivo font-bold text-lg text-white mb-2">Description</h3>
                        <p className="font-inter text-sm text-muted-foreground">
                          {extractLinkDescription(viewerDoc.content)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : viewerDoc.content_type === 'html' ? (
                  <HtmlRenderer content={viewerDoc.content} />
                ) : viewerDoc.file_url ? (
                  // Handle uploaded files (images, PDFs, videos)
                  <div className="space-y-4">
                    {viewerDoc.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <img 
                        src={viewerDoc.file_url} 
                        alt={viewerDoc.title}
                        className="max-w-full h-auto rounded-sm"
                      />
                    ) : viewerDoc.file_url.match(/\.(mp4|webm|mov)$/i) ? (
                      <video 
                        src={viewerDoc.file_url} 
                        controls 
                        className="max-w-full h-auto rounded-sm"
                      />
                    ) : viewerDoc.file_url.match(/\.pdf$/i) ? (
                      <iframe
                        src={viewerDoc.file_url}
                        className="w-full h-[600px] rounded-sm border border-white/10"
                        title={viewerDoc.title}
                      />
                    ) : (
                      <div className="bg-surface p-4 rounded-sm">
                        <p className="font-mono text-sm text-muted-foreground">
                          File type not supported for preview. Please download to view.
                        </p>
                        <a 
                          href={viewerDoc.file_url}
                          download
                          className="inline-flex items-center gap-2 mt-3 text-primary hover:underline"
                        >
                          <Download className="w-4 h-4" />
                          Download File
                        </a>
                      </div>
                    )}
                    {viewerDoc.content && (
                      <div className="mt-6 pt-6 border-t border-white/10">
                        <MarkdownRenderer content={viewerDoc.content} />
                      </div>
                    )}
                  </div>
                ) : (
                  <MarkdownRenderer content={viewerDoc.content} />
                )}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Markdown renderer
const MarkdownRenderer = ({ content }) => {
  const lines = content.split('\n');
  
  return (
    <div className="space-y-3">
      {lines.map((line, index) => {
        // Use a compound key: index + content prefix. The lines list is
        // derived from doc.content and doesn't reorder/insert mid-list, so
        // pure index would work — but compound keys are stable across small
        // edits and keep React happier.
        const k = `${index}-${line.slice(0, 24)}`;
        if (line.startsWith('# ')) {
          return <h1 key={k} className="font-chivo font-black text-2xl text-white mt-6 mb-4">{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={k} className="font-chivo font-bold text-xl text-white mt-4 mb-3">{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={k} className="font-chivo font-bold text-lg text-white mt-3 mb-2">{line.slice(4)}</h3>;
        }
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={k} className="font-inter font-bold text-white">{line.slice(2, -2)}</p>;
        }
        if (line.startsWith('- ') || line.startsWith('* ')) {
          return (
            <div key={k} className="flex items-start gap-2 ml-4">
              <span className="text-primary mt-1.5">•</span>
              <span className="font-inter text-sm text-muted-foreground">{line.slice(2)}</span>
            </div>
          );
        }
        if (/^\d+\.\s/.test(line)) {
          const match = line.match(/^(\d+)\.\s(.*)$/);
          if (match) {
            return (
              <div key={k} className="flex items-start gap-2 ml-4">
                <span className="font-mono text-primary">{match[1]}.</span>
                <span className="font-inter text-sm text-muted-foreground">{match[2]}</span>
              </div>
            );
          }
        }
        if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
          return <p key={k} className="font-inter text-sm text-muted-foreground italic">{line.slice(1, -1)}</p>;
        }
        if (line.trim() === '') {
          return <div key={k} className="h-2" />;
        }
        return <p key={k} className="font-inter text-sm text-muted-foreground leading-relaxed">{line}</p>;
      })}
    </div>
  );
};

// HTML renderer for Word documents
const HtmlRenderer = ({ content }) => {
  const styledContent = `
    <style>
      .doc-content h1 { font-size: 1.5rem; font-weight: 800; color: white; margin: 1.5rem 0 1rem 0; font-family: 'Chivo', sans-serif; }
      .doc-content h2 { font-size: 1.25rem; font-weight: 700; color: white; margin: 1.25rem 0 0.75rem 0; font-family: 'Chivo', sans-serif; }
      .doc-content h3 { font-size: 1.125rem; font-weight: 600; color: white; margin: 1rem 0 0.5rem 0; font-family: 'Chivo', sans-serif; }
      .doc-content p { color: #a1a1aa; margin: 0.5rem 0; line-height: 1.6; font-family: 'Inter', sans-serif; }
      .doc-content strong, .doc-content b { color: white; font-weight: 600; }
      .doc-content em, .doc-content i { font-style: italic; }
      .doc-content ul { list-style-type: disc; margin-left: 1.5rem; margin-top: 0.5rem; margin-bottom: 0.5rem; }
      .doc-content ol { list-style-type: decimal; margin-left: 1.5rem; margin-top: 0.5rem; margin-bottom: 0.5rem; }
      .doc-content li { color: #a1a1aa; margin: 0.25rem 0; line-height: 1.5; }
      .doc-content li::marker { color: #3b82f6; }
      .doc-content table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
      .doc-content th { background: #171717; color: white; padding: 0.75rem; text-align: left; border: 1px solid #262626; }
      .doc-content td { padding: 0.75rem; border: 1px solid #262626; color: #a1a1aa; }
      .doc-content a { color: #3b82f6; text-decoration: underline; }
    </style>
    <div class="doc-content">${content}</div>
  `;
  
  return (
    <div 
      className="html-document-content"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(styledContent) }}
    />
  );
};

export default DocumentsPage;
